import { useState, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  Plus, Trash2, Pin, PinOff, Star, StarOff,
  BookOpen, Archive, X, Check,
  Clock, StickyNote, MoreVertical, Edit3, Copy,
  Search, LayoutGrid,
} from 'lucide-react'

const SEED = [
  {
    id: 1, title: 'Project Kickoff Notes',
    content: 'Discussed timeline with the team. Q4 target confirmed. Need to finalize design specs by Friday. Backend team starts sprint planning next Monday. Budget approved for extra infrastructure.',
    color: '#4F46E5', tags: ['project', 'meeting'],
    pinned: true, starred: true, archived: false,
    createdAt: '2024-06-10T09:00:00', updatedAt: '2024-06-11T14:30:00',
  },
  {
    id: 2, title: 'API Integration Checklist',
    content: '✅ Auth endpoints\n✅ User CRUD\n⬜ Payment gateway\n⬜ Webhook setup\n⬜ Rate limiting docs\n⬜ Error handling review',
    color: '#06B6D4', tags: ['dev', 'checklist'],
    pinned: true, starred: false, archived: false,
    createdAt: '2024-06-09T11:00:00', updatedAt: '2024-06-10T16:00:00',
  },
  {
    id: 3, title: 'Design System Tokens',
    content: 'Colors: indigo-500 as primary, cyan-400 as secondary. Typography: Inter for body, JetBrains Mono for code. Radius: sm=6px, md=10px, lg=14px. Spacing scale: 4, 6, 8, 10, 16, 24px.',
    color: '#A855F7', tags: ['design', 'ui'],
    pinned: false, starred: true, archived: false,
    createdAt: '2024-06-08T10:00:00', updatedAt: '2024-06-09T09:15:00',
  },
  {
    id: 4, title: 'User Feedback Summary',
    content: 'Key feedback from last sprint review:\n- Dashboard load time too slow\n- Mobile layout needs improvement\n- Users love the dark theme\n- Export feature highly requested',
    color: '#10B981', tags: ['feedback', 'ux'],
    pinned: false, starred: false, archived: false,
    createdAt: '2024-06-07T15:30:00', updatedAt: '2024-06-07T15:30:00',
  },
  {
    id: 5, title: 'Q3 Revenue Goals',
    content: 'Target: ₹45L MRR by September. Current: ₹28L. Need 12 enterprise conversions. Upsell campaign planned for July. Partner channel to contribute 20% of new revenue.',
    color: '#F59E0B', tags: ['finance', 'goals'],
    pinned: false, starred: true, archived: false,
    createdAt: '2024-06-06T09:00:00', updatedAt: '2024-06-08T11:00:00',
  },
  {
    id: 6, title: 'Old Sprint Retro',
    content: 'Sprint 12 retrospective. Velocity: 42 points. Blockers: infra issues on day 3. Team morale good. Carry over: 3 tickets to next sprint.',
    color: '#EF4444', tags: ['sprint'],
    pinned: false, starred: false, archived: true,
    createdAt: '2024-05-20T10:00:00', updatedAt: '2024-05-22T17:00:00',
  },
  {
    id: 7, title: 'Onboarding Flow Ideas',
    content: 'Step 1: Welcome + branding. Step 2: Role selection. Step 3: First import wizard. Step 4: Dashboard tour. Consider skip option for power users. A/B test two variants.',
    color: '#06B6D4', tags: ['ux', 'product'],
    pinned: false, starred: false, archived: false,
    createdAt: '2024-06-05T14:00:00', updatedAt: '2024-06-06T10:30:00',
  },
  {
    id: 8, title: 'Vendor Contacts',
    content: 'AWS: support@aws.com\nRazorpay: integrations@razorpay.com\nTwilio: help@twilio.com\nSendgrid: support@sendgrid.com',
    color: '#4F46E5', tags: ['contacts', 'infra'],
    pinned: false, starred: false, archived: false,
    createdAt: '2024-06-04T11:00:00', updatedAt: '2024-06-04T11:00:00',
  },
]

const COLORS = [
  { hex: '#4F46E5', name: 'Indigo' },
  { hex: '#06B6D4', name: 'Cyan' },
  { hex: '#10B981', name: 'Green' },
  { hex: '#F59E0B', name: 'Amber' },
  { hex: '#EF4444', name: 'Red' },
  { hex: '#A855F7', name: 'Purple' },
]

function timeAgo(str) {
  const d = Math.floor((Date.now() - new Date(str)) / 1000)
  if (d < 60) return 'just now'
  if (d < 3600) return `${Math.floor(d / 60)}m ago`
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`
  return `${Math.floor(d / 86400)}d ago`
}

function excerpt(txt, n = 140) {
  return txt.length > n ? txt.slice(0, n).trimEnd() + '…' : txt
}

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
.nt3-page { display:flex; flex-direction:column; gap:18px; padding:0 }

.nt3-topbar {
  display:flex; align-items:flex-start; justify-content:space-between;
  padding:0; flex-wrap:wrap; gap:12px;
}
.nt3-topbar-sub { font-size:11px; color:var(--text3); margin:1rem 0 10px 0; }

.nt3-new-btn { display:flex; align-items:center; gap:7px; background:var(--accent); color:#fff; border:none; border-radius:var(--radius-md); padding:9px 18px; font-size:12px; font-weight:700; cursor:pointer; font-family:var(--font-body); transition:all .15s; box-shadow:0 2px 10px rgba(79,70,229,0.3); white-space:nowrap; }
.nt3-new-btn:hover { background:var(--accent-hover); transform:translateY(-1px); box-shadow:0 4px 16px rgba(79,70,229,0.4); }
.nt3-controls {
  display:flex; align-items:center; gap:12px; flex-wrap:wrap;
  background:var(--bg-card); border:1px solid var(--border); border-radius:var(--radius-lg);
  padding:10px 14px; backdrop-filter:blur(16px);
}
.nt3-tabs { display:flex; gap:4px; flex-wrap:wrap; }
.nt3-tab {
  display:flex; align-items:center; gap:6px; padding:7px 14px; border-radius:var(--radius-full);
  border:1px solid transparent; background:transparent; color:var(--text3);
  font-size:12px; font-weight:600; cursor:pointer; font-family:var(--font-body);
  transition:all .15s; white-space:nowrap;
}
.nt3-tab:hover  { color:var(--text1); background:var(--bg-hover); }
.nt3-tab.active { color:var(--accent-hover); background:var(--accent-glow-sm); border-color:var(--border); }
.nt3-tab-count {
  font-size:9px; font-weight:700; padding:1px 6px; border-radius:var(--radius-full);
  background:var(--bg-raised); border:1px solid var(--border); color:var(--text3);
}
.nt3-tab.active .nt3-tab-count { background:var(--accent); color:#fff; border-color:var(--accent); }

.nt3-search-wrap { position:relative; flex:1; min-width:160px; max-width:280px; margin-left:auto; }
.nt3-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--text3); pointer-events:none; }
.nt3-search-inp {
  background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-sm);
  color:var(--text1); font-family:var(--font-body); font-size:12px;
  padding:8px 10px 8px 32px; outline:none; width:100%; transition:border-color .15s, box-shadow .15s;
}
.nt3-search-inp:focus { border-color:var(--accent); box-shadow:0 0 0 3px rgba(79,70,229,0.10); }
.nt3-search-inp::placeholder { color:var(--text3); }

.nt3-grid { columns:4 240px; column-gap:14px; }
.nt3-grid-item { break-inside:avoid; margin-bottom:14px; }

.nt3-card {
  position:relative; border-radius:var(--radius-lg);
  border:1px solid var(--border); border-left:3px solid var(--card-color, var(--accent));
  background:var(--bg-card); backdrop-filter:blur(16px);
  padding:14px 15px; cursor:pointer;
  transition:transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .18s, border-color .18s;
  display:flex; flex-direction:column; gap:9px;
}
.nt3-card:hover { transform:translateY(-3px); box-shadow:0 10px 28px rgba(0,0,0,.3); border-color:var(--border-active); border-left-color:var(--card-color, var(--accent)); }

.nt3-card-top    { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
.nt3-card-title  { font-size:13px; font-weight:700; color:var(--text1); letter-spacing:-0.01em; line-height:1.3; }
.nt3-card-badges { display:flex; align-items:center; gap:5px; flex-shrink:0; }
.nt3-card-text   { font-size:11.5px; color:var(--text2); line-height:1.65; white-space:pre-line; }
.nt3-card-footer { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:6px; margin-top:auto; }
.nt3-card-tags   { display:flex; gap:4px; flex-wrap:wrap; }
.nt3-tag         { font-size:9px; font-weight:600; padding:2px 8px; border-radius:var(--radius-full); background:var(--bg-raised); border:1px solid var(--border); color:var(--text3); }
.nt3-card-time   { font-size:10px; color:var(--text4); display:flex; align-items:center; gap:3px; flex-shrink:0; }

.nt3-card-menu-wrap { position:relative; }
.nt3-menu-btn { background:none; border:none; color:var(--text3); cursor:pointer; padding:3px; border-radius:4px; display:flex; align-items:center; transition:color .12s, background .12s; opacity:0; }
.nt3-card:hover .nt3-menu-btn { opacity:1; }
.nt3-menu-btn:hover { color:var(--text1); background:var(--bg-raised); }

.nt3-dropdown {
  z-index:9999;
  background:var(--bg-raised);
  border:1px solid var(--border-active);
  border-radius:var(--radius-md);
  min-width:150px;
  box-shadow:var(--shadow-lg);
  overflow:hidden;
  animation:nt3-pop .12s ease;
}
@keyframes nt3-pop { from{opacity:0;transform:scale(.95) translateY(-4px)} to{opacity:1;transform:scale(1) translateY(0)} }
.nt3-dd-item { display:flex; align-items:center; gap:9px; padding:9px 13px; font-size:12px; font-weight:500; color:var(--text2); cursor:pointer; transition:all .1s; border:none; background:none; width:100%; text-align:left; font-family:var(--font-body); }
.nt3-dd-item:hover { background:var(--bg-hover); color:var(--text1); }
.nt3-dd-item.danger:hover { background:var(--color-red-glow); color:var(--color-red); }
.nt3-dd-divider { height:1px; background:var(--border); margin:3px 0; }

.nt3-section-label { font-size:10px; font-weight:700; color:var(--text3); text-transform:uppercase; letter-spacing:.1em; margin-bottom:10px; display:flex; align-items:center; gap:7px; }
.nt3-section-label::after { content:''; flex:1; height:1px; background:var(--border); }

.nt3-empty { text-align:center; padding:64px 20px; color:var(--text3); }
.nt3-empty-icon { width:56px; height:56px; border-radius:16px; background:var(--bg-raised); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; margin:0 auto 16px; color:var(--text3); }
.nt3-empty-title { font-size:15px; font-weight:700; color:var(--text2); margin-bottom:6px; }
.nt3-empty-sub   { font-size:12px; color:var(--text3); margin-bottom:20px; }

/* ── MODAL BACKDROP — same as common Modal.jsx ── */
body.modal-open { overflow: hidden; }

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(2, 6, 23, .5);
  backdrop-filter: blur(10px) saturate(110%);
  -webkit-backdrop-filter: blur(10px) saturate(110%);
  display: grid;
  place-items: center;
  padding: var(--space-md);
  animation: nt3-fade .18s ease;
}
@keyframes nt3-fade { from{opacity:0} to{opacity:1} }

.nt3-modal {
  width: min(580px, 100%);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-active);
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg), 0 20px 48px rgba(2, 6, 23, .25);
  animation: nt3-rise .2s cubic-bezier(.32,.72,0,1);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  overflow-y: auto;
}
@keyframes nt3-rise { from{transform:translateY(10px) scale(.99);opacity:.6} to{transform:translateY(0) scale(1);opacity:1} }

.nt3-modal-topbar { height:2px; border-radius:var(--radius-lg) var(--radius-lg) 0 0; flex-shrink:0; }
.nt3-modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px 0; }
.nt3-modal-head-title { font-size:13px; font-weight:700; color:var(--text1); display:flex; align-items:center; gap:8px; }
.nt3-modal-actions { display:flex; gap:4px; }
.nt3-icon-btn { width:30px; height:30px; border-radius:var(--radius-sm); background:none; border:none; cursor:pointer; color:var(--text3); display:flex; align-items:center; justify-content:center; transition:color .12s, background .12s; font-family:var(--font-body); }
.nt3-icon-btn:hover       { color:var(--text1); background:var(--bg-hover); }
.nt3-icon-btn.star-active { color:var(--color-amber); }
.nt3-icon-btn.pin-active  { color:var(--accent); }
.nt3-modal-body { padding:18px 20px 6px; display:flex; flex-direction:column; gap:16px; flex:1; }
.nt3-field  { display:flex; flex-direction:column; gap:5px; }
.nt3-label  { font-size:9.5px; font-weight:700; color:var(--text3); text-transform:uppercase; letter-spacing:.1em; }
.nt3-title-inp { background:transparent; border:none; border-bottom:1px solid var(--border); color:var(--text1); font-family:var(--font-body); font-size:18px; font-weight:700; padding:6px 0; outline:none; width:100%; letter-spacing:-0.02em; transition:border-color .15s; }
.nt3-title-inp:focus       { border-bottom-color:var(--accent); }
.nt3-title-inp::placeholder{ color:var(--text4); }
.nt3-title-inp.err         { border-bottom-color:var(--color-red); }
.nt3-content-inp { background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-md); color:var(--text1); font-family:var(--font-body); font-size:13px; padding:12px 14px; outline:none; width:100%; resize:vertical; min-height:160px; line-height:1.7; transition:border-color .15s, box-shadow .15s; }
.nt3-content-inp:focus       { border-color:var(--accent); box-shadow:0 0 0 3px rgba(79,70,229,.1); }
.nt3-content-inp::placeholder{ color:var(--text3); }
.nt3-colors { display:flex; gap:8px; flex-wrap:wrap; }
.nt3-color-swatch { width:26px; height:26px; border-radius:50%; cursor:pointer; border:2px solid transparent; outline:2px solid transparent; outline-offset:2px; transition:transform .15s, outline-color .15s; }
.nt3-color-swatch:hover    { transform:scale(1.1); }
.nt3-color-swatch.selected { border-color:#fff; outline-color:inherit; transform:scale(1.15); }
.nt3-tag-box { display:flex; gap:6px; flex-wrap:wrap; align-items:center; background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-md); padding:7px 12px; transition:border-color .15s, box-shadow .15s; }
.nt3-tag-box:focus-within { border-color:var(--accent); box-shadow:0 0 0 3px rgba(79,70,229,.1); }
.nt3-tag-pill { display:inline-flex; align-items:center; gap:4px; font-size:10px; font-weight:600; padding:3px 8px; background:var(--accent-glow-sm); color:var(--accent-hover); border:1px solid var(--accent); border-radius:var(--radius-full); }
.nt3-tag-pill button { background:none; border:none; color:inherit; cursor:pointer; display:flex; align-items:center; padding:0; line-height:1; }
.nt3-tag-raw { background:none; border:none; outline:none; color:var(--text1); font-family:var(--font-body); font-size:12px; min-width:90px; flex:1; }
.nt3-tag-raw::placeholder { color:var(--text3); }
.nt3-modal-foot {
  padding:14px 20px 18px; display:flex; justify-content:flex-end; gap:8px;
  border-top:1px solid var(--border);
  background: linear-gradient(0deg, rgba(255,255,255,.012), transparent);
}
.nt3-cancel-btn { background:none; border:1px solid var(--border); color:var(--text2); border-radius:var(--radius-sm); padding:8px 18px; font-size:12px; font-weight:600; cursor:pointer; font-family:var(--font-body); transition:all .15s; min-width:96px; }
.nt3-cancel-btn:hover { border-color:var(--border-active); color:var(--text1); }
.nt3-save-btn { background:var(--accent); color:#fff; border:none; border-radius:var(--radius-sm); padding:8px 20px; font-size:12px; font-weight:700; cursor:pointer; font-family:var(--font-body); transition:all .15s; display:flex; align-items:center; gap:6px; box-shadow:0 2px 8px rgba(79,70,229,.3); min-width:96px; }
.nt3-save-btn:hover { background:var(--accent-hover); transform:translateY(-1px); }

.nt3-toast { position:fixed; bottom:24px; right:24px; z-index:9999; background:var(--bg-raised); border:1px solid var(--border-active); color:var(--text1); border-radius:var(--radius-md); padding:11px 18px; font-size:13px; font-weight:600; box-shadow:var(--shadow-lg); animation:soft-rise .22s ease; display:flex; align-items:center; gap:8px; }

@media(max-width:1200px) { .nt3-grid { columns:3 220px; } }
@media(max-width:900px)  { .nt3-page { padding:0 14px 32px; } .nt3-topbar { padding:14px 14px 0; } .nt3-grid { columns:2 200px; } }
@media(max-width:640px)  { .nt3-controls { flex-direction:column; align-items:stretch; } .nt3-tabs { width:100%; } .nt3-tab { flex:1; justify-content:center; } .nt3-search-wrap { max-width:100%; margin-left:0; } .nt3-grid { columns:1; } }
@media(max-width:420px)  { .nt3-page { padding:0 10px 28px; } .nt3-tabs { flex-wrap:wrap; } .nt3-tab { flex:1 1 45%; } .modal-backdrop { align-items:flex-end; padding:0; } .nt3-modal { border-radius:12px 12px 0 0; width:100%; } }
`

function useCSS() {
  useEffect(() => {
    const id = 'nt3-css'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id
    el.textContent = CSS
    document.head.appendChild(el)
    return () => document.getElementById(id)?.remove()
  }, [])
}

/* ─── Card menu ───────────────────────────────────────────────────────────── */
function CardMenu({ note, onEdit, onPin, onStar, onDuplicate, onArchive, onDelete }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, right: 0 })
  const ref = useRef(null)
  const btnRef = useRef(null)

  useEffect(() => {
    function h(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  function handleOpen(e) {
    e.stopPropagation()
    if (!open) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      })
    }
    setOpen(v => !v)
  }

  const items = [
    { icon: Edit3, label: 'Edit', action: () => { onEdit(note); setOpen(false) } },
    { icon: note.pinned ? PinOff : Pin, label: note.pinned ? 'Unpin' : 'Pin', action: () => { onPin(note.id); setOpen(false) } },
    { icon: note.starred ? StarOff : Star, label: note.starred ? 'Unstar' : 'Star', action: () => { onStar(note.id); setOpen(false) } },
    { icon: Copy, label: 'Duplicate', action: () => { onDuplicate(note); setOpen(false) } },
    { divider: true },
    { icon: Archive, label: note.archived ? 'Restore' : 'Archive', action: () => { onArchive(note.id); setOpen(false) } },
    { icon: Trash2, label: 'Delete', action: () => { onDelete(note.id); setOpen(false) }, danger: true },
  ]

  return (
    <div className="nt3-card-menu-wrap" ref={ref}>
      <button
        ref={btnRef}
        className="nt3-menu-btn"
        onClick={handleOpen}
      >
        <MoreVertical size={14} />
      </button>
      {open && createPortal(
        <div
          className="nt3-dropdown"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          onClick={e => e.stopPropagation()}
        >
          {items.map((item, i) =>
            item.divider
              ? <div key={i} className="nt3-dd-divider" />
              : <button
                key={i}
                className={`nt3-dd-item${item.danger ? ' danger' : ''}`}
                onClick={item.action}
              >
                <item.icon size={13} />{item.label}
              </button>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

/* ─── Note card ───────────────────────────────────────────────────────────── */
function NoteCard({ note, onEdit, onPin, onStar, onDuplicate, onArchive, onDelete }) {
  return (
    <div className="nt3-grid-item">
      <div className="nt3-card" style={{ '--card-color': note.color }} onClick={() => onEdit(note)}>
        <div className="nt3-card-top">
          <div className="nt3-card-title">{note.title}</div>
          <div className="nt3-card-badges" onClick={e => e.stopPropagation()}>
            {note.pinned && <Pin size={11} color="var(--accent)" style={{ opacity: .75 }} />}
            {note.starred && <Star size={11} color="var(--color-amber)" fill="var(--color-amber)" />}
            <CardMenu
              note={note} onEdit={onEdit} onPin={onPin} onStar={onStar}
              onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete}
            />
          </div>
        </div>
        <div className="nt3-card-text">{excerpt(note.content)}</div>
        <div className="nt3-card-footer">
          <div className="nt3-card-tags">
            {note.tags.slice(0, 3).map(t => <span key={t} className="nt3-tag">#{t}</span>)}
            {note.tags.length > 3 && <span className="nt3-tag">+{note.tags.length - 3}</span>}
          </div>
          <div className="nt3-card-time"><Clock size={9} />{timeAgo(note.updatedAt)}</div>
        </div>
      </div>
    </div>
  )
}

/* ─── Note Modal — createPortal + same backdrop as common Modal.jsx ───────── */
function NoteModal({ note, onClose, onSave }) {
  const isNew = !note?.id
  const [form, setForm] = useState(
    note?.id
      ? { ...note }
      : { title: '', content: '', color: '#4F46E5', tags: [], pinned: false, starred: false, archived: false }
  )
  const [tagInput, setTagInput] = useState('')
  const [err, setErr] = useState(false)

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [])

  function addTag(v) {
    const val = v.trim().toLowerCase().replace(/^#/, '')
    if (val && !form.tags.includes(val)) setForm(f => ({ ...f, tags: [...f.tags, val] }))
    setTagInput('')
  }
  function removeTag(t) { setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) })) }
  function tagKey(e) {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput) }
    if (e.key === 'Backspace' && !tagInput && form.tags.length)
      setForm(f => ({ ...f, tags: f.tags.slice(0, -1) }))
  }
  function handleSave() {
    if (!form.title.trim()) { setErr(true); setTimeout(() => setErr(false), 1500); return }
    const now = new Date().toISOString()
    onSave({ ...form, id: note?.id || Date.now(), createdAt: note?.createdAt || now, updatedAt: now })
    onClose()
  }

  return createPortal(
    <div className="modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="nt3-modal" role="dialog" aria-modal="true">

        <div className="nt3-modal-topbar" style={{ background: form.color }} />

        <div className="nt3-modal-head">
          <div className="nt3-modal-head-title">
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: form.color, flexShrink: 0 }} />
            {isNew ? 'New Note' : 'Edit Note'}
          </div>
          <div className="nt3-modal-actions">
            <button
              className={`nt3-icon-btn${form.starred ? ' star-active' : ''}`}
              title={form.starred ? 'Unstar' : 'Star'}
              onClick={() => setForm(f => ({ ...f, starred: !f.starred }))}
            >
              <Star size={14} fill={form.starred ? 'currentColor' : 'none'} />
            </button>
            <button
              className={`nt3-icon-btn${form.pinned ? ' pin-active' : ''}`}
              title={form.pinned ? 'Unpin' : 'Pin'}
              onClick={() => setForm(f => ({ ...f, pinned: !f.pinned }))}
            >
              {form.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            </button>
            <button className="nt3-icon-btn" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div className="nt3-modal-body">
          <input
            className={`nt3-title-inp${err ? ' err' : ''}`}
            placeholder="Note title…"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            autoFocus
          />
          <textarea
            className="nt3-content-inp"
            placeholder="Start writing… (line breaks supported)"
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          />
          <div className="nt3-field">
            <div className="nt3-label">Color</div>
            <div className="nt3-colors">
              {COLORS.map(c => (
                <div
                  key={c.hex}
                  className={`nt3-color-swatch${form.color === c.hex ? ' selected' : ''}`}
                  style={{ background: c.hex, outlineColor: c.hex }}
                  title={c.name}
                  onClick={() => setForm(f => ({ ...f, color: c.hex }))}
                />
              ))}
            </div>
          </div>
          <div className="nt3-field">
            <div className="nt3-label">Tags</div>
            <div className="nt3-tag-box">
              {form.tags.map(t => (
                <span key={t} className="nt3-tag-pill">
                  #{t}
                  <button onClick={() => removeTag(t)}><X size={9} /></button>
                </span>
              ))}
              <input
                className="nt3-tag-raw"
                placeholder={form.tags.length ? 'Add tag…' : 'Add tag, press Enter…'}
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={tagKey}
                onBlur={() => tagInput && addTag(tagInput)}
              />
            </div>
          </div>
        </div>

        <div className="nt3-modal-foot">
          <button className="nt3-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="nt3-save-btn" onClick={handleSave}>
            <Check size={13} />
            {isNew ? 'Create Note' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ─── Main ────────────────────────────────────────────────────────────────── */
export default function NotesPage() {
  useCSS()
  const [notes, setNotes] = useState(SEED)
  const [tab, setTab] = useState('all')
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(null)
  const [toast, setToast] = useState(null)

  function showToast(msg, color = 'var(--color-green)') {
    setToast({ msg, color })
    setTimeout(() => setToast(null), 2400)
  }

  const counts = useMemo(() => ({
    all: notes.filter(n => !n.archived).length,
    starred: notes.filter(n => n.starred && !n.archived).length,
    pinned: notes.filter(n => n.pinned && !n.archived).length,
    archived: notes.filter(n => n.archived).length,
  }), [notes])

  const visible = useMemo(() => {
    let list = notes.filter(n =>
      tab === 'archived' ? n.archived :
        tab === 'starred' ? (n.starred && !n.archived) :
          tab === 'pinned' ? (n.pinned && !n.archived) :
            !n.archived
    )
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(n =>
        n.title.toLowerCase().includes(q) ||
        n.content.toLowerCase().includes(q) ||
        n.tags.some(t => t.includes(q))
      )
    }
    list = [...list].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    if (tab !== 'archived') list = [...list.filter(n => n.pinned), ...list.filter(n => !n.pinned)]
    return list
  }, [notes, tab, search])

  const pinnedNotes = visible.filter(n => n.pinned)
  const otherNotes = visible.filter(n => !n.pinned)

  function saveNote(note) {
    setNotes(prev =>
      prev.find(n => n.id === note.id)
        ? prev.map(n => n.id === note.id ? note : n)
        : [note, ...prev]
    )
    showToast(note.id ? 'Note updated' : 'Note created')
  }
  function deleteNote(id) { setNotes(p => p.filter(n => n.id !== id)); showToast('Deleted', 'var(--color-red)') }
  function togglePin(id) { setNotes(p => p.map(n => n.id === id ? { ...n, pinned: !n.pinned } : n)) }
  function toggleStar(id) { setNotes(p => p.map(n => n.id === id ? { ...n, starred: !n.starred } : n)) }
  function duplicateNote(note) {
    const now = new Date().toISOString()
    setNotes(p => [{ ...note, id: Date.now(), title: note.title + ' (copy)', createdAt: now, updatedAt: now, pinned: false }, ...p])
    showToast('Duplicated')
  }
  function archiveNote(id) {
    setNotes(p => p.map(n => n.id === id ? { ...n, archived: !n.archived, pinned: false } : n))
    showToast('Archived')
  }

  const TABS = [
    { id: 'all', label: 'All', icon: LayoutGrid, count: counts.all },
    { id: 'starred', label: 'Starred', icon: Star, count: counts.starred },
    { id: 'pinned', label: 'Pinned', icon: Pin, count: counts.pinned },
    { id: 'archived', label: 'Archived', icon: Archive, count: counts.archived },
  ]

  const cardProps = {
    onEdit: setModal, onPin: togglePin, onStar: toggleStar,
    onDuplicate: duplicateNote, onArchive: archiveNote, onDelete: deleteNote,
  }

  const titleMap = { all: 'All Notes', starred: 'Starred Notes', pinned: 'Pinned Notes', archived: 'Archived Notes' }
  const subMap = {
    all: 'Everything in one place',
    starred: 'Your favourites, easy to find',
    pinned: 'Pinned for quick access',
    archived: 'Out of the way, not deleted',
  }

  return (
    <div>
      <div className="nt3-topbar">
        <div>
          <div className="page-title">{titleMap[tab]}</div>
          <div className="nt3-topbar-sub">{visible.length} note{visible.length !== 1 ? 's' : ''} · {subMap[tab]}</div>
        </div>
        <button className="nt3-new-btn" onClick={() => setModal({})}>
          <Plus size={14} /> New Note
        </button>
      </div>

      <div className="nt3-page">
        <div className="nt3-controls">
          <div className="nt3-tabs">
            {TABS.map(t => (
              <button key={t.id} className={`nt3-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                <t.icon size={13} /> {t.label}
                <span className="nt3-tab-count">{t.count}</span>
              </button>
            ))}
          </div>
          <div className="nt3-search-wrap">
            <Search size={13} className="nt3-search-icon" />
            <input
              className="nt3-search-inp"
              placeholder="Search notes, tags…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="nt3-empty">
            <div className="nt3-empty-icon"><StickyNote size={24} /></div>
            <div className="nt3-empty-title">
              {search ? 'No matching notes' : tab === 'archived' ? 'Nothing archived' : 'No notes here yet'}
            </div>
            <div className="nt3-empty-sub">
              {search ? 'Try a different search term.'
                : tab === 'archived' ? 'Archive a note from the card menu to move it here.'
                  : tab === 'starred' ? 'Star a note to save it here for quick access.'
                    : tab === 'pinned' ? 'Pin a note to keep it at the top.'
                      : 'Create your first note to get started.'}
            </div>
            {tab === 'all' && !search && (
              <button className="nt3-new-btn" style={{ margin: '0 auto' }} onClick={() => setModal({})}>
                <Plus size={14} /> Create Note
              </button>
            )}
          </div>
        ) : (
          <>
            {tab === 'all' && pinnedNotes.length > 0 && (
              <div>
                <div className="nt3-section-label"><Pin size={10} />Pinned</div>
                <div className="nt3-grid">
                  {pinnedNotes.map(n => <NoteCard key={n.id} note={n} {...cardProps} />)}
                </div>
              </div>
            )}
            {(tab !== 'all' || otherNotes.length > 0) && (
              <div>
                {tab === 'all' && pinnedNotes.length > 0 && (
                  <div className="nt3-section-label" style={{ marginTop: 8 }}><BookOpen size={10} />Other</div>
                )}
                <div className="nt3-grid">
                  {(tab === 'all' ? otherNotes : visible).map(n => <NoteCard key={n.id} note={n} {...cardProps} />)}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {modal !== null && (
        <NoteModal note={modal} onClose={() => setModal(null)} onSave={saveNote} />
      )}

      {toast && (
        <div className="nt3-toast" style={{ borderColor: toast.color, color: toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  )
}